import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Send, Paperclip, Link as LinkIcon, X, Image as ImgIcon, Search, MoreVertical, Trash2, ShieldOff, Inbox, AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import api from "@/lib/api";

async function fileToBase64(file, max = 1400) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement("canvas");
        let { width, height } = img;
        if (width > max || height > max) { const s = Math.min(max / width, max / height); width = Math.floor(width * s); height = Math.floor(height * s); }
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = reject; img.src = r.result;
    };
    r.onerror = reject; r.readAsDataURL(file);
  });
}

function linkify(text) {
  if (!text) return null;
  const urlRe = /(https?:\/\/[^\s]+)/g;
  return text.split(urlRe).map((part, i) => {
    if (urlRe.test(part)) return <a key={i} href={part} target="_blank" rel="noreferrer" className="underline text-eagle-gold">{part}</a>;
    return part;
  });
}

export default function ChatTab() {
  const [params, setParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [tab, setTab] = useState("inbox");
  const endRef = useRef(null);

  const loadThreads = async () => { const { data } = await api.get("/admin/chat/threads", { params: { q, spam: tab === "spam" } }); setThreads(data); };
  const loadThread = async (id) => { const { data } = await api.get(`/admin/chat/threads/${id}`); setActive(data.thread); setMsgs(data.messages); };

  useEffect(() => { loadThreads(); const id = setInterval(loadThreads, 10000); return () => clearInterval(id); /* eslint-disable-next-line */ }, [q, tab]);
  useEffect(() => { if (!active) return; const id = setInterval(() => loadThread(active.thread_id), 6000); return () => clearInterval(id); }, [active?.thread_id]); // eslint-disable-line
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  useEffect(() => {
    const t = params.get("thread");
    if (t && threads.some(x => x.thread_id === t)) { loadThread(t); params.delete("thread"); setParams(params); }
  // eslint-disable-next-line
  }, [threads, params]);

  const toggleSpam = async (thread) => {
    const { data } = await api.post(`/admin/chat/threads/${thread.thread_id}/spam`);
    toast.success(data.is_spam ? "Moved to spam" : "Restored to inbox");
    if (active?.thread_id === thread.thread_id) setActive(null);
    loadThreads();
  };
  const deleteThread = async (thread) => {
    if (!window.confirm(`Permanently delete conversation with ${thread.customer_name}? This can't be undone.`)) return;
    await api.delete(`/admin/chat/threads/${thread.thread_id}`);
    toast.success("Conversation deleted");
    if (active?.thread_id === thread.thread_id) { setActive(null); setMsgs([]); }
    loadThreads();
  };

  const send = async () => {
    if ((!text.trim() && attachments.length === 0) || !active) return;
    await api.post(`/admin/chat/threads/${active.thread_id}/messages`, { content: text.trim(), attachments });
    setText(""); setAttachments([]); loadThread(active.thread_id); loadThreads();
  };

  const onImage = async (file) => { const b64 = await fileToBase64(file); setAttachments([...attachments, { type: "image", url: b64, name: file.name }]); };
  const onLink = () => { const u = window.prompt("Link URL:"); if (u) setAttachments([...attachments, { type: "link", url: u, name: u }]); };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Chat</h1>
        <p className="text-sm text-muted-foreground mt-1">Real-time messaging with customers · attach images & links.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[70vh]">
        <Card className="p-0 overflow-hidden flex flex-col">
          <div className="flex border-b border-border">
            <button onClick={() => { setTab("inbox"); setActive(null); }} data-testid="chat-tab-inbox" className={`flex-1 py-2.5 text-sm font-medium flex items-center justify-center gap-1.5 transition ${tab === "inbox" ? "text-eagle-gold border-b-2 border-eagle-gold" : "text-muted-foreground hover:text-foreground"}`}>
              <Inbox className="h-4 w-4" />Inbox
            </button>
            <button onClick={() => { setTab("spam"); setActive(null); }} data-testid="chat-tab-spam" className={`flex-1 py-2.5 text-sm font-medium flex items-center justify-center gap-1.5 transition ${tab === "spam" ? "text-eagle-gold border-b-2 border-eagle-gold" : "text-muted-foreground hover:text-foreground"}`}>
              <AlertTriangle className="h-4 w-4" />Spam
            </button>
          </div>
          <div className="p-3 border-b border-border relative">
            <Search className="h-4 w-4 absolute left-6 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search threads&hellip;" className="pl-10 h-9" data-testid="admin-chat-search" />
          </div>
          <div className="flex-1 overflow-y-auto">
            {threads.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">{tab === "spam" ? "Spam is empty." : "No threads."}</div>}
            {threads.map(t => (
              <div key={t.thread_id} className={`group flex items-start gap-2 p-3 border-b border-border hover:bg-secondary transition-colors ${active?.thread_id === t.thread_id ? "bg-secondary" : ""}`}>
                <button onClick={() => loadThread(t.thread_id)} className="flex-1 text-left min-w-0" data-testid={`admin-thread-${t.thread_id}`}>
                  <div className="flex items-center justify-between mb-0.5">
                    <div className="font-medium text-sm truncate">{t.customer_name}</div>
                    {t.unread_admin > 0 && !t.is_spam && <Badge className="bg-eagle-gold text-eagle-ink border-0 text-xs">{t.unread_admin}</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{t.last_message || "—"}</div>
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-1 rounded hover:bg-secondary/80 opacity-0 group-hover:opacity-100 transition" data-testid={`thread-menu-${t.thread_id}`}><MoreVertical className="h-4 w-4 text-muted-foreground" /></button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => toggleSpam(t)} data-testid={`thread-spam-${t.thread_id}`}>
                      <ShieldOff className="h-4 w-4 mr-2" /> {t.is_spam ? "Restore to inbox" : "Mark as spam"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => deleteThread(t)} className="text-red-500 focus:text-red-500" data-testid={`thread-delete-${t.thread_id}`}>
                      <Trash2 className="h-4 w-4 mr-2" /> Delete conversation
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        </Card>
        <Card className="md:col-span-2 p-0 flex flex-col overflow-hidden">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">Select a conversation</div>
          ) : (
            <>
              <div className="p-4 border-b border-border">
                <div className="font-display font-semibold">{active.customer_name}</div>
                <div className="text-xs text-muted-foreground">{active.customer_email}</div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-background">
                {msgs.map(m => (
                  <div key={m.message_id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl text-sm ${m.sender === "admin" ? "bg-eagle-gold text-eagle-ink rounded-br-sm" : "bg-secondary rounded-bl-sm"}`}>
                      {m.attachments?.length > 0 && (
                        <div className={`p-2 space-y-2 ${m.content ? "border-b border-black/10" : ""}`}>
                          {m.attachments.map((a, i) => a.type === "image" ? (
                            <a key={i} href={a.url} target="_blank" rel="noreferrer" className="block"><img src={a.url} alt="" className="rounded-lg max-w-full max-h-64 object-cover" /></a>
                          ) : (
                            <a key={i} href={a.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-2 py-1 rounded bg-black/10"><LinkIcon className="h-3 w-3" /><span className="text-xs truncate">{a.name}</span></a>
                          ))}
                        </div>
                      )}
                      {m.content && <div className="px-3.5 py-2 whitespace-pre-wrap">{linkify(m.content)}</div>}
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              {attachments.length > 0 && (
                <div className="px-3 pt-2 flex flex-wrap gap-2 border-t border-border">
                  {attachments.map((a, i) => (
                    <div key={i} className="relative">
                      {a.type === "image" ? <img src={a.url} alt="" className="h-14 w-14 rounded object-cover" /> : <div className="h-14 px-3 rounded bg-secondary text-xs flex items-center gap-1"><LinkIcon className="h-3 w-3" />{a.name}</div>}
                      <button onClick={() => setAttachments(attachments.filter((_, j) => j !== i))} className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="p-3 border-t border-border flex items-end gap-2">
                <label className="cursor-pointer">
                  <Button variant="ghost" size="sm" type="button" data-testid="chat-attach-image" asChild><span><ImgIcon className="h-4 w-4" /></span></Button>
                  <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && onImage(e.target.files[0])} />
                </label>
                <Button variant="ghost" size="sm" onClick={onLink} data-testid="chat-attach-link"><LinkIcon className="h-4 w-4" /></Button>
                <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Type reply… links auto-detect" className="resize-none" data-testid="admin-chat-input" onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
                <Button onClick={send} disabled={!text.trim() && attachments.length === 0} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="admin-chat-send"><Send className="h-4 w-4" /></Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

export { linkify };
